// What the user has open in this tab, for agents outside the board (Features 1, 20 Oct): the page, step, post,
// client, idea or widget in view, by kind and id only. The tab says it to the board as it changes, and read_board
// tells an outside agent what it is and how long ago it was said; the chats on the board are told more as they send.

import { useEffect } from "react";
import { kindOf } from "../../shared/boardKinds";
import { openOn, useDocUi } from "../boards/doc/state";
import { useCalUi } from "../boards/calendar/state";
import { usePipeUi } from "../boards/pipeline/state";
import { useMapUi } from "../boards/mindmap/state";
import { openOf, useSlidesUi } from "../boards/slides/state";
import { slidesOf } from "../../shared/slides";
import { boardEntries, currentBoard, get, useStore } from "./store";
import { setViewOpen, type ViewOpen } from "./sync";

function openThing(): ViewOpen | null {
  const s = get();
  const board = currentBoard(s);
  if (!board || board.archived) return null;
  const kind = kindOf(board);
  if (kind === "doc" || kind === "plan") {
    const page = openOn(boardEntries(s, board.id).filter((e) => e.t === "page"), useDocUi.getState().open[board.id]);
    return page ? { kind: "page", id: page.id } : null;
  }
  if (kind === "calendar") {
    const id = useCalUi.getState().open[board.id];
    return id && s.entries[id]?.boardId === board.id ? { kind: "post", id } : null;
  }
  if (kind === "pipeline") {
    const id = usePipeUi.getState().open;
    return id && s.entries[id]?.boardId === board.id ? { kind: "deal", id } : null;
  }
  if (kind === "slides") {
    const slide = openOf(slidesOf(boardEntries(s, board.id)), useSlidesUi.getState().open[board.id]);
    return slide ? { kind: "slide", id: slide.id } : null;
  }
  if (kind === "mindmap") {
    const id = useMapUi.getState().selected[board.id];
    if (id && s.entries[id]?.boardId === board.id) return { kind: "idea", id };
  }
  // a widget, when one alone is selected
  const one = s.selection.length === 1 ? s.items[s.selection[0]] : null;
  return one && one.kind === "widget" && one.boardId === board.id ? { kind: "widget", id: one.id } : null;
}

/** Keeps the board told what is open in this tab while the app runs. */
export function useReportOpen() {
  useEffect(() => {
    const report = () => setViewOpen(openThing());
    const offs = [useStore.subscribe(report), useDocUi.subscribe(report), useCalUi.subscribe(report), usePipeUi.subscribe(report), useMapUi.subscribe(report), useSlidesUi.subscribe(report)];
    report();
    return () => offs.forEach((off) => off());
  }, []);
}
